import React, { useEffect, useState } from "react";
import Quagga from "quagga";
import { useNavigate } from "react-router-dom";
import "./ScanPage.css";
import products from "../hair_products.json";
import { getProductImage } from "../constants";
import ingredientData from "../final_ingredient_tr_data.json";

function matchProductByBarcode(barcode) {
    for (const [category, items] of Object.entries(products)) {
        const product = items.find((p) => p.barcode === barcode);
        if (product) return { product, category };
    }
    return null;
}

const getIngredientDetails = (ingredientName) => {
    const formattedName = ingredientName.toUpperCase();
    return ingredientData[formattedName] || {};
};

const getIngredientColor = (score) => {
    if (score >= 6.0) return "#ff5252"; // Red - High Concern
    if (score >= 3.0) return "#ffb74d"; // Yellow - Moderate Concern
    return "#81c784"; // Green - Safe
};

const formatIngredientName = (name) => {
    return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

function IngredientList({ ingredientAnalysis }) {
    return (
        <div className="scan-ingredients-container">
            <h3 className="scan-ingredients-title">İçindekiler</h3>

            <div className="scan-ingredients-text">
                {ingredientAnalysis.map((item, index) => {
                    const details = getIngredientDetails(item);
                    const backgroundColor = getIngredientColor(parseFloat(details.score || "0"));

                    return (
                        <span
                            key={index}
                            className="scan-ingredient-word"
                            style={{ backgroundColor }}
                        >
                            {formatIngredientName(item)}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

export default function ScanPage() {
    const [detectedProduct, setDetectedProduct] = useState(null);
    const [detectedCategory, setDetectedCategory] = useState(null);
    const [scannedBarcode, setScannedBarcode] = useState("");
    const [image, setImage] = useState(null);
    const [error, setError] = useState("");
    const [isUploadingIngredients, setIsUploadingIngredients] = useState(false);
    const [ingredientAnalysis, setIngredientAnalysis] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        let isQuaggaInitialized = false;

        async function startScanner() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
                if (stream) {
                    Quagga.init(
                        {
                            inputStream: {
                                name: "Live",
                                type: "LiveStream",
                                target: document.querySelector("#barcode-video"),
                                constraints: { facingMode: "environment" },
                            },
                            decoder: {
                                readers: ["code_128_reader", "ean_reader", "upc_reader", "code_39_reader"],
                            },
                            locate: true,
                        },
                        (err) => {
                            if (!err) {
                                Quagga.start();
                                isQuaggaInitialized = true;
                            }
                        }
                    );

                    Quagga.onDetected((res) => {
                        const code = res.codeResult.code;
                        if (code && code !== scannedBarcode) {
                            setScannedBarcode(code);
                            const result = matchProductByBarcode(code);
                            if (result) {
                                setDetectedProduct(result.product);
                                setDetectedCategory(result.category);
                                setError("");
                                saveScannedBarcode(result.product.barcode);
                            } else {
                                setDetectedProduct(null);
                                setDetectedCategory(null);
                                setError("Bu ürünü bulamadık! İçerik bilgilerini fotoğraflayıp yükleyerek bize yardımcı olabilir misin?");
                            }
                            Quagga.stop();
                        }
                    });
                }
            } catch (error) {
                setError("Kamera erişimi reddedildi. Lütfen izinleri kontrol edin.");
            }
        }

        navigator.permissions
            .query({ name: "camera" })
            .then((permissionStatus) => {
                if (permissionStatus.state === "granted") {
                    startScanner();
                } else if (permissionStatus.state === "prompt") {
                    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
                        .then(() => startScanner())
                        .catch(() => setError("Kamera izni reddedildi."));
                }
            });

        return () => {
            if (isQuaggaInitialized) {
                Quagga.stop();
                Quagga.offDetected(() => { });
            }
        };
    }, [scannedBarcode]);

    const saveScannedBarcode = (barcode) => {
        let scannedBarcodes = JSON.parse(localStorage.getItem("scannedBarcodes")) || [];
        scannedBarcodes = scannedBarcodes.filter((b) => b !== barcode);
        scannedBarcodes.unshift(barcode);
        scannedBarcodes = scannedBarcodes.slice(0, 10);
        localStorage.setItem("scannedBarcodes", JSON.stringify(scannedBarcodes));
    };

    const handleNavigation = (barcode) => {
        if (barcode) navigate(`/product/${barcode}`);
    };

    const handleIngredientsUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploadingIngredients(true);
        setIngredientAnalysis([]);
        setError("");

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await fetch("https://api.whatsin-app.com/extract-text", {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Sunucu hatası");
            }

            const result = await response.json();

            if (result.ingredients && result.ingredients.length > 0) {
                const filteredIngredients = result.ingredients.filter((item) => item.trim().length > 0 && item.length < 150);
                setIngredientAnalysis(filteredIngredients);
            } else {
                setIngredientAnalysis([]);
                setError("Analiz başarısız oldu. Lütfen tekrar deneyin.");
            }
        } catch (err) {
            setIngredientAnalysis([]);
            setError(`Bir hata oluştu: ${err.message}`);
        } finally {
            setIsUploadingIngredients(false);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setError("");
        const reader = new FileReader();
        reader.onload = () => {
            setImage(reader.result);
            Quagga.decodeSingle(
                {
                    src: reader.result,
                    decoder: {
                        readers: ["code_128_reader", "ean_reader", "upc_reader", "code_39_reader"],
                    },
                    locate: true,
                },
                (result) => {
                    if (result && result.codeResult) {
                        const code = result.codeResult.code;
                        setScannedBarcode(code);
                        const productResult = matchProductByBarcode(code);
                        if (productResult) {
                            setDetectedProduct(productResult.product);
                            setDetectedCategory(result.category);
                            setError("");
                        } else {
                            setDetectedProduct(null);
                            setDetectedCategory(null);
                            setError("Bu ürünü bulamadık! İçerik bilgilerini fotoğraflayıp yükleyerek bize yardımcı olabilir misin?");
                        }
                    } else {
                        setDetectedProduct(null);
                        setDetectedCategory(null);
                        setError("Barkod algılanamadı.");
                    }
                }
            );
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="scan-page-new">
            <h2 className="scan-title-new">Barkod Tara veya Yükle</h2>
            <div className="barcode-video-container-new">
                <div id="barcode-video" className="barcode-video-new" />
            </div>
            <div className="upload-container-new">
                <input
                    type="file"
                    accept="image/*"
                    id="file-input"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                />
                <button
                    className="upload-button-new"
                    onClick={() => document.getElementById("file-input").click()}
                >
                    Fotoğraf Yükle
                </button>
                {image && (
                    <div className="scanned-image-wrap-new">
                        <img src={image} alt="Scanned" className="scanned-image-new" />
                    </div>
                )}
            </div>
            {detectedProduct && detectedCategory && (
                <div className="detected-product-new" onClick={() => handleNavigation(detectedProduct.barcode)}>
                    <img
                        src={getProductImage(detectedCategory, detectedProduct.barcode)}
                        alt={detectedProduct.name}
                        className="product-image-new"
                    />
                    <div className="detected-name-new">{detectedProduct.name}</div>
                    <div className="detected-brand-new">{detectedProduct.brand}</div>
                </div>
            )}
            {error && (
                <div className="ingredients-upload-section-new">
                    <p>{error}</p>
                    <input
                        type="file"
                        accept="image/*"
                        id="ingredients-input"
                        onChange={handleIngredientsUpload}
                        style={{ display: "none" }}
                    />
                    <button
                        className="upload-ingredients-button-new"
                        onClick={() => document.getElementById("ingredients-input").click()}
                    >
                        İçindekileri Yükle
                    </button>
                </div>
            )}
            {isUploadingIngredients && (
                <div className="loading-overlay">
                    <div className="loader"></div>
                    <p className="loading-text">Analiz Ediliyor...</p>
                </div>
            )}
            {!isUploadingIngredients && ingredientAnalysis.length > 0 && (
                <IngredientList ingredientAnalysis={ingredientAnalysis} />
            )}
        </div>
    );
}
